import React, { useState, useEffect } from "react";
import { Upload, Button, message, Table, Card, Typography, Empty, Spin } from "antd";
import { UploadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import { RcFile } from "antd/es/upload";
import type { ColumnsType } from "antd/es/table";
import CustomExcelIcon from "../../../../assets/icons/CustomExcelIcon";

const { Title, Text } = Typography;

interface StepOneProps {
  onNext: (data: StepOneData) => void;
  existingData?: StepOneData | null;
}

export interface StepOneData {
  dataFile: RcFile;
  headers: string[];
  previewData: any[];
}

const StepOne: React.FC<StepOneProps> = ({ onNext, existingData }) => {
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isParsingFile, setIsParsingFile] = useState(false);

  // Initialize with existing data if available  
  useEffect(() => {
    if (existingData) {
      setFileList([existingData.dataFile]);
      setHeaders(existingData.headers);
      setPreviewData(existingData.previewData);
    }
  }, [existingData]);  

  const parseFile = (file: RcFile) => {
    setIsParsingFile(true);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: 10, // Preview first 10 rows
      complete: (results) => {
        if (results.data && results.data.length > 0 && results.meta.fields) {
          const headers = results.meta.fields;
          setHeaders(headers);
          setPreviewData(results.data);
        } else {
          message.error("Failed to parse CSV file. Please check the file format.");
        }
        setIsParsingFile(false);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        message.error("Error parsing CSV file. Please check the file format.");
        setIsParsingFile(false); 
      },
    });
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.error("Please select a CSV file first");
      return;
    }

    setUploading(true);

    // Parse the CSV file to get headers
    Papa.parse(fileList[0], {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.data && results.data.length > 0 && results.meta.fields) {
          const headers = results.meta.fields;
          
          // Proceed to next step with file and headers
          onNext({
            dataFile: fileList[0],
            headers: headers,
            previewData: previewData.length > 0 ? previewData : results.data.slice(0, 10)
          });
        } else {
          message.error("Failed to parse CSV file. Please check the file format.");
        }
        setUploading(false);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        message.error("Error parsing CSV file. Please check the file format.");
        setUploading(false);
      },
    });
  };

  const props = {
    onRemove: () => {
      setFileList([]);
      setHeaders([]);
      setPreviewData([]);
    },
    beforeUpload: (file: RcFile) => {
      // Validate file type
      const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCsv) {
        message.error('You can only upload CSV files!');
        return false;
      }

      setFileList([file]);
      parseFile(file);
      return false;
    },
    fileList,
  };

  // Generate table columns from headers
  const columns: ColumnsType<any> = headers.map(header => ({
    title: header,
    dataIndex: header,
    key: header,
    ellipsis: true,
  }));

  return (
    <div className="flex flex-col space-y-6">
      <Card className="shadow-sm">
        <Title level={4} className="mb-2">Upload Historic Data</Title>
        <Text className="text-gray-600 block mb-6">
          Upload a CSV file containing your historic campaign data. This data will be used to optimize your bid strategy.
        </Text>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6">
          <div className="flex items-start">
            <CustomExcelIcon className="text-blue-500 text-xl mt-1 mr-3" />
            <div>
              <Text strong className="text-blue-700 block mb-1">File Requirements</Text>
              <Text className="text-blue-600 block text-sm">
                • File must be in CSV format<br />
                • First row should contain column headers<br />
                • Include columns for customer identifiers and target variables<br />
                • Optional: demographic, geographic, and device data columns
              </Text>
            </div>
          </div>
        </div>

        <Upload 
          {...props} 
          maxCount={1} 
          accept=".csv"
          className="mb-6"
          listType="picture"
        >
          <Button icon={<UploadOutlined />} size="large" type="primary" ghost>
            Select CSV File
          </Button>
        </Upload>

        {isParsingFile && (
          <div className="text-center py-8">
            <Spin tip="Parsing file..." />
          </div>
        )}

        {!isParsingFile && headers.length > 0 && previewData.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center mb-3">
              <CheckCircleOutlined className="text-green-500 mr-2" />
              <Text strong className="text-green-700">File successfully parsed</Text>
            </div>
            
            <Card 
              title="Data Preview" 
              size="small" 
              className="mb-4"
              extra={<Text type="secondary">Showing first {previewData.length} rows</Text>}
            >
              <div className="max-h-[300px] overflow-auto">
                <Table 
                  dataSource={previewData} 
                  columns={columns}
                  size="small"
                  pagination={false}
                  bordered
                  scroll={{ x: 'max-content' }}
                  rowKey={(record, index) => index?.toString() || '0'}
                />
              </div>
            </Card>
          </div>
        )}

        {!isParsingFile && headers.length === 0 && !fileList.length && (
          <Empty 
            description="No file uploaded yet" 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
            className="my-8"
          />
        )}

        <div className="mt-6 flex justify-end">
          <Button 
            type="primary" 
            onClick={handleUpload} 
            loading={uploading}
            disabled={fileList.length === 0 || headers.length === 0}
            size="large"
          >
            Continue with this file
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default StepOne;